import styled from '@emotion/styled'
import { CartItemGroup } from '@open-tender/types'
import { useModifierGroup } from '@open-tender/utils'
import { selectDisplaySettings, useAppSelector } from '@open-tender/cloud'
import { Heading, Text } from 'components'
import React from 'react'
import { CornerDownRight, NavArrowDown, NavArrowUp } from '../../icons'
import { useTheme } from '@emotion/react'

const MenuItemGroupHeaderAltView = styled.div<{
  isChild?: boolean
  isCollapsed?: boolean
}>`
  display: flex;
  margin: ${(props) => (props.isChild ? '0' : '0 0 1rem')};
  justify-content: space-between;
  align-items: flex-end;
  cursor: pointer;
  user-select: none;
  padding-bottom: ${(props) => (props.isCollapsed ? '1rem' : '0')};
  border-bottom: ${(props) =>
    props.isCollapsed ? `1px solid ${props.theme.colors.dark}` : 'none'};

  &:hover {
    opacity: 0.8;
  }
`

const MenuItemGroupHeaderAltCollapseIcon = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  color: ${(props) => props.theme.colors.primary};
  transition: transform 0.2s ease-in-out;
  gap: 0.5rem;
`

const MenuItemGroupHeaderAltArrow = styled.span`
  position: relative;
  top: 0.3rem;
`

const MenuItemGroupHeaderAltName = styled(Heading)<{ isChild?: boolean }>`
  margin-bottom: 0.5rem;
  line-height: 1.2;
  font-size: ${(props) =>
    props.isChild ? props.theme.fonts.sizes.main : props.theme.fonts.sizes.big};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    font-size: ${(props) =>
      props.isChild
        ? props.theme.fonts.sizes.small
        : props.theme.fonts.sizes.main};
  }
`

const MenuItemGroupHeaderAltNameQuantity = styled.span<{
  belowMin?: boolean
  belowIncluded?: boolean
}>`
  padding: 0 0 0 0.5rem;
  ${(props) =>
    props.belowMin
      ? `color: ${props.theme.colors.error};`
      : props.belowIncluded
      ? `color: ${props.theme.colors.success};`
      : ''}
`

const MenuItemGroupHeaderAltDescription = styled.p`
  font-size: ${(props) => props.theme.fonts.sizes.small};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    font-size: ${(props) => props.theme.fonts.sizes.xSmall};
  }
`

const MenuItemGroupHeaderAlt = ({
  group,
  isChild = false,
  isCollapsed = false,
  onToggleCollapse,
}: {
  group: CartItemGroup
  isChild?: boolean
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}) => {
  const { colors } = useTheme()
  const displaySettings = useAppSelector(selectDisplaySettings)
  const { name, description, count, quantity, min, included } =
    useModifierGroup(group, displaySettings)

  // Get selected modifier names when collapsed
  const selectedModifiers = group.options
    .filter((option) => option.quantity > 0)
    .map((option) => option.name)
    .join(', ')

  return (
    <MenuItemGroupHeaderAltView
      tabIndex={0}
      isChild={isChild}
      isCollapsed={isCollapsed}
      onClick={onToggleCollapse}
    >
      <div>
        <MenuItemGroupHeaderAltName
          className={
            quantity < min || (quantity ?? 0) < (included ?? 0)
              ? 'required'
              : undefined
          }
          as="h3"
          isChild={isChild}
        >
          {isChild ? (
            <MenuItemGroupHeaderAltArrow>
              <CornerDownRight size={18} />
            </MenuItemGroupHeaderAltArrow>
          ) : null}{' '}
          {name}
          <MenuItemGroupHeaderAltNameQuantity
            belowMin={quantity < min}
            belowIncluded={(quantity ?? 0) < (included ?? 0)}
          >
            ({count})
          </MenuItemGroupHeaderAltNameQuantity>
        </MenuItemGroupHeaderAltName>
        {isCollapsed && selectedModifiers ? (
          <MenuItemGroupHeaderAltDescription>
            {selectedModifiers}
          </MenuItemGroupHeaderAltDescription>
        ) : !isCollapsed && description ? (
          <MenuItemGroupHeaderAltDescription>
            {description}
          </MenuItemGroupHeaderAltDescription>
        ) : null}
      </div>
      {onToggleCollapse && (
        <MenuItemGroupHeaderAltCollapseIcon>
          <Text size="small" color="tertiary">
            Customize
          </Text>
          {isCollapsed ? (
            <NavArrowDown size={20} color={colors.tertiary} />
          ) : (
            <NavArrowUp size={20} color={colors.tertiary} />
          )}
        </MenuItemGroupHeaderAltCollapseIcon>
      )}
    </MenuItemGroupHeaderAltView>
  )
}

export default MenuItemGroupHeaderAlt
